import { type PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot, type Tone } from './shared';

/**
 * Jack-in-the-Box's shots (the Jester set's gun, ADR-0160 amended): a playing card thrown spinning, a comic "POW!" of a
 * star and confetti at the muzzle, confetti bursting where it lands; and the flourish's confetti (`jester_bit`, white:
 * tinted each piece its colour).
 */

/** Confetti's colours: the toy box's and a party's (never the team's keys). */
const PARTY = ['r', 'y', 'l', 'p', 'w', 'o', 'i', 'Y'] as const;

/** A heart, its pixels from its top-left: the pip a card shows whichever way it is turned (5 × 4 px, too small to turn). */
const HEART = ['rr.rr', 'rrrrr', '.rrr.', '..R..'] as const;

/** A card of half-size (hw, hh) at (cx, cy) turned by `a`: white, shaded on its lower right edge, a red heart. */
const card = (x: number, y: number, cx: number, cy: number, hw: number, hh: number, a: number): Tone => {
  const [dx, dy] = [x - cx, y - cy];
  const [u, v] = [dx * Math.cos(a) + dy * Math.sin(a), -dx * Math.sin(a) + dy * Math.cos(a)];
  if (Math.abs(u) > hw || Math.abs(v) > hh) return null;
  // Its corners cut round.
  if (Math.abs(u) > hw - 0.9 && Math.abs(v) > hh - 0.9) return null;
  const pip = HEART[Math.floor(y - cy + 2)]?.[Math.floor(x - cx + 2.5)];
  if (pip && pip !== '.') return pip as Tone;
  // Its edge in shade where it turns from the light (screen space: lower right).
  return dx + dy > 0 && (Math.abs(u) > hw - 1 || Math.abs(v) > hh - 1) ? '5' : 'w';
};

/** Whether the pixel centred on (x, y) is on a piece of confetti: a little rectangle `w` × `h` round (cx, cy). */
const bit = (x: number, y: number, cx: number, cy: number, w: number, h: number): boolean =>
  x >= cx - w / 2 && x < cx + w / 2 && y >= cy - h / 2 && y < cy + h / 2;

const thrown = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const k = card(x, y, 21, 8, 4, 5.5, (f * Math.PI) / 4);
    if (k) return k;
    // Confetti flicking off behind it.
    const trail = [
      [13 - (f % 2), 6 + (f % 3), 'y'],
      [9 + (f % 2), 10 - (f % 3), 'p'],
      [5 + ((f * 2) % 3), 7 + (f % 2), 'l'],
    ] as const;
    for (const [tx, ty, tone] of trail) if (bit(x, y, tx, ty, 2, 2)) return tone;
    return null;
  });

/** A comic starburst round (cx, cy): `n` points between radii r0 and r1, turned by `a`, white-hot in, red at its rim. */
const star = (x: number, y: number, cx: number, cy: number, r0: number, r1: number, n: number, a: number): Tone => {
  const [dx, dy] = [x - cx, y - cy];
  const d = Math.hypot(dx, dy);
  const t = (((Math.atan2(dy, dx) - a) / (Math.PI * 2)) * n) % 1;
  const reach = r0 + (r1 - r0) * Math.abs(((t + 1) % 1) * 2 - 1) ** 1.6;
  if (d >= reach) return null;
  const k = d / reach;
  return k < 0.35 ? 'w' : k < 0.62 ? 'y' : k < 0.84 ? 'Y' : 'r';
};

/** The muzzle's confetti: [angle (radians off the shot's line), distance on frame 1, colour, wide]. */
const SPRAY = [
  [-0.75, 11, 0, true],
  [-0.4, 15, 1, false],
  [-0.1, 13, 2, true],
  [0.22, 16, 3, false],
  [0.5, 12, 4, true],
  [0.85, 10, 5, false],
  [0.05, 9, 6, false],
  [-0.55, 8, 7, true],
] as const;

const pow = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    if (f === 0) return star(x, y, 11, 20, 4.5, 10.5, 9, 0.2);
    if (f === 1) {
      const s = star(x, y, 13, 20, 3, 7, 9, 0.55);
      if (s) return s === 'w' ? 'y' : s;
    }
    for (const [a, d, k, wide] of SPRAY) {
      const r = 4 + d * (f === 1 ? 1 : 1.7);
      const [px, py] = [4 + Math.cos(a) * r, 20 + Math.sin(a) * r + (f === 2 ? 2 : 0)];
      if (f === 2 && k % 2 === 1) continue;
      if (bit(x, y, px, py, wide ? 3 : 2, wide ? 2 : 3)) return PARTY[k] as Tone;
    }
    return null;
  });

/** The burst's confetti: [angle, how far it flies, colour, wide]. */
const BURST = Array.from({ length: 14 }, (_, i) => {
  const a = (i / 14) * Math.PI * 2 + (i % 3) * 0.17;
  return [a, 0.75 + ((i * 7) % 5) * 0.08, i % PARTY.length, i % 2 === 0] as const;
});

const confetti = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    if (f === 0) {
      const s = star(x, y, 22, 22, 2.5, 9.5, 4, 0) ?? star(x, y, 22, 22, 2, 5.5, 4, Math.PI / 4);
      if (s) return s;
    }
    if (f === 1 && Math.hypot(x - 22, y - 22) < 2.2) return 'w';
    const R = [0, 9, 14, 17.5][f] as number;
    const fall = [0, 0, 1.5, 4][f] as number;
    for (const [i, [a, k, tone, wide]] of BURST.entries()) {
      if (f === 0 || (f === 3 && i % 3 === 0)) continue;
      const r = R * k;
      // Each turns over as it flies: wide, then on its edge.
      const flat = (i + f) % 3 !== 2;
      const [w, h] = flat ? (wide ? [3, 2] : [2, 3]) : [2, 1];
      if (bit(x, y, 22 + Math.cos(a) * r, 22 + Math.sin(a) * r * 0.85 + fall, w, h)) return PARTY[tone] as Tone;
    }
    return null;
  });

/** The flourish's confetti: a white piece (tinted) turning over: flat, tilting, on its edge, tilting back. */
const piece = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [w, h] = ([
      [4, 3],
      [4, 2],
      [4, 1],
      [3, 2],
    ][f] ?? [4, 3]) as [number, number];
    if (!bit(x, y, 4, 4, w, h)) return null;
    return y > 4 + h / 2 - 1 && h > 1 ? '5' : 'w';
  });

export default [
  sprite({
    name: 'jackbox_shot',
    ...shot,
    notes: `Jack-in-the-Box's bullet (ADR-0160 amended): a playing card thrown spinning (white, cut corners, its lower right
edge in shade, a red heart; an eighth of a turn a frame, so 4 frames loop its half turn), a few bits of confetti
flicking off behind it. Head right. Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: the heart is 5 px and doesn't turn
with the card.`,
    anims: { idle: { fps: 16, frames: frames4(thrown) } },
  }),
  sprite({
    name: 'jackbox_muzzle',
    ...muzzle,
    notes: `Jack-in-the-Box's muzzle flash (ADR-0160 amended): a comic "POW!": a nine-pointed starburst (white-hot, yellow,
gold, a red rim) out of the clown's nose, then a smaller star and a spray of confetti flying on, then the confetti alone
dropping. Points right. Rubric (4×): S4 R5 P4 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(pow) } },
  }),
  sprite({
    name: 'jackbox_burst',
    ...burst,
    notes: `Jack-in-the-Box's hit (ADR-0160 amended): confetti bursting: a four-pointed star flash, then fourteen pieces flung
out in a ring, turning over as they fly and falling as they slow, the last few. 4 frames. Rubric (4×): S4 R4 P4 L4 C4
A4 F4. Weakest: on the lane its orange and red pieces are low in contrast.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(confetti) } },
  }),
  sprite({
    name: 'jester_bit',
    group: 'fx',
    size: [8, 8],
    origin: [4, 4],
    density: 4,
    notes: `The Jester's knock-out confetti (ADR-0160 amended): one white piece turning over (flat, tilting, on its edge,
tilting back), its foot in shade; the flourish tints each its colour. Rubric (4×): S4 R4 P5 L4 C5 A4 F4.`,
    anims: { idle: { fps: 12, frames: frames4(piece) } },
  }),
];
